import { useCallback, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

// The address is written directly rather than through the router: the router
// applies a change a moment later, and a text box bound to it would drop
// letters while someone types. Everything bound to the address is told at
// once instead.
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  window.addEventListener('popstate', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('popstate', listener);
  };
};

// Path and query together, so a change of screen is seen as one.
const currentAddress = () =>
  `${window.location.pathname}${window.location.search}`;

// What each screen's query last was. While the router is still showing a
// screen the address has already left, that screen keeps its own query
// rather than taking the next screen's.
const lastSearch = new Map<string, string>();

const writeSearch = (params: URLSearchParams) => {
  const query = params.toString();
  const { pathname, hash } = window.location;
  window.history.replaceState(
    window.history.state,
    '',
    `${pathname}${query ? `?${query}` : ''}${hash}`,
  );
  listeners.forEach((listener) => listener());
};

/** The part of the address after `?`, as it is right now for this screen. */
export const useUrlSearch = () => {
  // Also follow the router, for a move between screens.
  const { pathname, search: routed } = useLocation();
  useSyncExternalStore(subscribe, currentAddress);

  if (window.location.pathname === pathname) {
    lastSearch.set(pathname, window.location.search);
    return window.location.search;
  }
  return lastSearch.get(pathname) ?? routed;
};

type UrlValue = string | number | boolean | null;

interface Options {
  /**
   * Go back to the first page when this value changes, as a search or a
   * filter must. Names the page parameter to drop; `true` means `page`.
   */
  resetPage?: boolean | string;
}

/**
 * `useState` kept in the address bar (`?page=2&search=ali`), so a list comes
 * back as it was left after a refresh, the Back button, or a shared link.
 * The value's type follows `initial`; a value equal to it is left out of the
 * address.
 */
type Setter<T> = (value: T | ((current: T) => T)) => void;

export function useUrlState(
  key: string,
  initial: number,
  options?: Options,
): [number, Setter<number>];
export function useUrlState(
  key: string,
  initial: boolean,
  options?: Options,
): [boolean, Setter<boolean>];
export function useUrlState(
  key: string,
  initial: string,
  options?: Options,
): [string, Setter<string>];
export function useUrlState(
  key: string,
  initial: null,
  options?: Options,
): [string | null, Setter<string | null>];
export function useUrlState<T extends UrlValue>(
  key: string,
  initial: T,
  options: Options = {},
): [T, Setter<T>] {
  const search = useUrlSearch();
  const pageKey =
    options.resetPage === true ? 'page' : options.resetPage || '';

  const parse = useCallback(
    (params: URLSearchParams): T => {
      const raw = params.get(key);
      if (raw === null) return initial;
      if (typeof initial === 'number') {
        const number = Number(raw);
        return (Number.isFinite(number) ? number : initial) as T;
      }
      if (typeof initial === 'boolean') return (raw === '1') as T;
      return raw as T;
    },
    [key, initial],
  );

  const value = parse(new URLSearchParams(search));

  const setValue = useCallback(
    (next: T | ((current: T) => T)) => {
      // Read from the address itself, not from this render: several setters
      // called one after another must each see the one before.
      const params = new URLSearchParams(window.location.search);
      const current = parse(params);
      const resolved =
        typeof next === 'function'
          ? (next as (current: T) => T)(current)
          : next;
      if (resolved === current) return;

      if (resolved === initial || resolved === null || resolved === '') {
        params.delete(key);
      } else if (typeof resolved === 'boolean') {
        params.set(key, resolved ? '1' : '0');
      } else {
        params.set(key, String(resolved));
      }
      if (pageKey) params.delete(pageKey);

      writeSearch(params);
    },
    [key, initial, pageKey, parse],
  );

  return [value, setValue];
}

export default useUrlState;
